/**
 * Monitor de OS — a tela de parede da oficina. Substitui o `Bubble.html`.
 *
 * Três problemas resolvidos:
 *  • OS cujo aparelho não casa com nenhuma palavra-chave ia para lugar nenhum
 *    e sumia da tela. Agora existe a coluna "Não atribuído".
 *  • Não havia atualização automática: a TV da oficina exigia F5 na mão.
 *  • O agrupamento por técnico vivia dentro do HTML; agora vem do servidor.
 *
 * Duas leituras da mesma fila, escolhidas pelo switch do cabeçalho:
 *  • Bolinhas — agrupadas por técnico, é a visão da parede.
 *  • Kanban   — uma coluna por status da OS, para acompanhar o fluxo.
 *
 * As duas usam a mesma pele do resto do sistema: fundo creme, texto `stone` e
 * os tons terrosos de `lib/paleta`. A parede era a única tela em slate escuro
 * com as cores de sinalização saturadas — ficava de fora da paleta e obrigava
 * cada componente a carregar um `escuro` só para ela.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from '@tanstack/react-router';
import {
  OS_STATUS,
  STATUS_INDEFINIDO,
  type ColunaMonitor,
  type OsMonitor,
  type OsStatus,
} from '@tcm/shared';
import { useMonitor, useMoverStatus } from '../features/queries';
import { AppShell, Cabecalho, ErroNaTela } from '../components/layout';
import { Cascata } from '../components/animados';
import { ModalOs } from '../components/ModalOs';
import { cx, Esqueleto, Vazio } from '../components/ui';
import { descreverDias, formatarData, formatarDataHora } from '../lib/format';
import { tomStatus, type TomStatus } from '../lib/paleta';
import { usePanHorizontal } from '../lib/pan';
import { usePonteiroGrosso } from '../lib/ponteiro';

const PARADA_HA_MUITO = 15; // dias

/* ------------------------------------------------------------------ */
/* Preferência de visualização                                         */
/* ------------------------------------------------------------------ */

type Visao = 'bolinhas' | 'kanban';

const CHAVE_VISAO = 'tcm:monitor:visao';

/**
 * A TV da oficina fica ligada sozinha o dia inteiro: a escolha precisa
 * sobreviver ao recarregamento. `localStorage` pode lançar (Safari privado),
 * então nenhuma das duas pontas assume que existe.
 */
function useVisao() {
  const [visao, setVisao] = useState<Visao>(() => {
    try {
      return localStorage.getItem(CHAVE_VISAO) === 'kanban' ? 'kanban' : 'bolinhas';
    } catch {
      return 'bolinhas';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(CHAVE_VISAO, visao);
    } catch {
      /* preferência não persiste; a tela continua funcionando */
    }
  }, [visao]);

  return [visao, setVisao] as const;
}

export function Monitor() {
  const { data, isLoading, error, refetch, dataUpdatedAt } = useMonitor();
  const [visao, setVisao] = useVisao();

  // O monitor se atualiza sozinho a cada 30 s. Enquanto o banco está fora do
  // ar, cada ciclo recomeça e `isLoading` volta a ser verdadeiro — se o
  // esqueleto tivesse prioridade, a tela ficaria eternamente "carregando" e o
  // operador nunca leria o motivo. Erro ganha da carga.
  const mostrarEsqueleto = isLoading && !error;

  return (
    <AppShell creme>
      <Cabecalho
        titulo="Monitor de Ordens de Serviço"
        subtitulo={
          data
            ? `${data.total} OS em aberto · atualizado às ${new Date(dataUpdatedAt).toLocaleTimeString('pt-BR')}`
            : error
              ? 'Sem contato com o servidor'
              : 'Carregando…'
        }
        acao={<SeletorVisao visao={visao} aoTrocar={setVisao} />}
      />

      {error && (
        <div className="mb-6">
          <ErroNaTela erro={error} aoTentar={() => void refetch()} />
        </div>
      )}

      {mostrarEsqueleto && (
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-2xl bg-white p-6 ring-1 ring-creme-200">
              <Esqueleto linhas={4} />
            </div>
          ))}
        </div>
      )}

      {data && (visao === 'kanban' ? <Kanban colunas={data.colunas} /> : <PorTecnico colunas={data.colunas} />)}

      {data && data.total === 0 && (
        <Vazio titulo="Tudo em ordem!" descricao="Nenhuma ordem de serviço em aberto no momento." />
      )}
    </AppShell>
  );
}

function SeletorVisao({ visao, aoTrocar }: { visao: Visao; aoTrocar: (v: Visao) => void }) {
  const opcoes: Array<{ id: Visao; rotulo: string }> = [
    { id: 'bolinhas', rotulo: 'Por técnico' },
    { id: 'kanban', rotulo: 'Kanban por status' },
  ];

  return (
    <div
      role="group"
      aria-label="Modo de visualização"
      // No celular ocupa a linha inteira e as duas opções dividem a largura em
      // dois alvos grandes; a partir de `sm` volta a ser a pílula compacta.
      className="flex w-full rounded-full bg-creme-100 p-1 ring-1 ring-creme-200 sm:inline-flex sm:w-auto"
    >
      {opcoes.map((op) => (
        <button
          key={op.id}
          type="button"
          onClick={() => aoTrocar(op.id)}
          aria-pressed={visao === op.id}
          className={cx(
            'flex-1 rounded-full px-4 py-2 text-sm font-semibold transition-colors sm:flex-none sm:py-1.5',
            visao === op.id
              ? 'bg-white text-stone-800 shadow-sm ring-1 ring-creme-300'
              : 'text-stone-500 hover:text-stone-800',
          )}
        >
          {op.rotulo}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Visão 1 — bolinhas agrupadas por técnico                            */
/* ------------------------------------------------------------------ */

function PorTecnico({ colunas }: { colunas: ColunaMonitor[] }) {
  // Coluna zerada não ocupa espaço no monitor — "Não atribuído" com 0 OS só
  // roubava um terço da tela. Com tudo zerado a lista some e quem responde é
  // o `Vazio` da rota, que já cobre esse caso.
  const comOrdens = colunas.filter((coluna) => coluna.ordens.length > 0);

  return (
    // Flex em vez de grid: a grade prendia as colunas às células da esquerda e
    // deixava um vão à direita quando o número de técnicos não fecha a linha.
    // As larguras reproduzem `md:grid-cols-2`/`xl:grid-cols-3` descontando a
    // parte do `gap-6` (1.5rem) que cabe a cada coluna.
    <div className="flex flex-wrap justify-center gap-6">
      {comOrdens.map((coluna) => (
        <section
          key={coluna.tecnicoId ?? 'sem-tecnico'}
          className="w-full rounded-2xl bg-creme-50 p-4 ring-1 ring-creme-200 sm:p-5 md:w-[calc(50%-0.75rem)] xl:w-[calc(33.333%-1rem)]"
        >
          <header className="mb-4 flex items-baseline justify-between gap-2 border-b border-creme-200 pb-3">
            <h2 className="truncate text-lg font-semibold text-stone-800">{coluna.tecnicoNome}</h2>
            <span className="shrink-0 rounded-full bg-creme-100 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-stone-500 ring-1 ring-creme-300">
              {coluna.ordens.length}
            </span>
          </header>

          <Cascata
            // `justify-center` no celular: em uma coluna só cabem três
            // bolinhas por linha, e alinhadas à esquerda a última linha
            // incompleta deixava um vão grande de um lado só.
            className="flex flex-wrap justify-center gap-4 sm:justify-start"
            chave={`${coluna.tecnicoId}-${coluna.ordens.length}`}
          >
            {coluna.ordens.map((os) => (
              <Bolinha key={os.osNumber} os={os} />
            ))}
          </Cascata>
        </section>
      ))}
    </div>
  );
}

function Bolinha({ os }: { os: OsMonitor }) {
  const parada = (os.diasEmAberto ?? 0) >= PARADA_HA_MUITO;
  const primeiroNome = os.nome.split(' ')[0] ?? '';
  // Mesmo tom do cartão do kanban: a bolinha e o filete da coluna passam a
  // dizer a mesma coisa com a mesma cor.
  const tom = tomStatus(os.status);

  return (
    <Link
      to="/os/$osNumber"
      params={{ osNumber: String(os.osNumber) }}
      className="group flex w-24 flex-col items-center gap-1.5 text-center"
      title={`OS ${os.osNumber} — ${os.statusRotulo} — ${os.aparelho ?? 'aparelho não informado'} — entrada ${formatarDataHora(os.entrada)}`}
    >
      <span className="w-full truncate text-xs font-medium text-stone-500">{primeiroNome}</span>

      <span
        className="flex h-14 w-24 flex-col items-center justify-center rounded-2xl text-white shadow-md transition-transform group-hover:scale-110"
        style={{ backgroundColor: tom.forte }}
      >
        <span className="text-2xl font-bold leading-none tabular-nums">{os.osNumber}</span>
        <span className="mt-1 w-20 truncate text-[10px] uppercase tracking-wide opacity-90">
          {os.aparelho ?? '—'}
        </span>
      </span>

      {/* OS parada há muito tempo ganha destaque — é o dado que o monitor
          precisava mostrar e não mostrava. O âmbar da marca é o mesmo do selo
          "parada" do quadro kanban. */}
      <span
        className={
          parada ? 'text-[11px] font-semibold text-brand-700' : 'text-[11px] text-stone-400'
        }
      >
        {descreverDias(os.diasEmAberto)}
      </span>
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/* Visão 2 — kanban por status                                         */
/* ------------------------------------------------------------------ */

/** A OS carrega o técnico da coluna de origem: no kanban ele vira etiqueta. */
interface OsNoQuadro extends OsMonitor {
  tecnicoNome: string;
}

/**
 * As etapas do quadro são os status do catálogo, na ordem do catálogo.
 *
 * Status que finalizam a OS ficam de fora porque o servidor nunca os devolve
 * (`monitor()` filtra por `finalizaOs`) — uma coluna "Retirado" seria sempre
 * zero. "Indefinido" só aparece quando existe OS sem status válido.
 */
const ETAPAS: OsStatus[] = OS_STATUS.filter((s) => !s.finalizaOs).sort((a, b) => a.ordem - b.ordem);

/** Cartões renderizados por vez em cada coluna (ver `ColunaKanban`). */
const LOTE = 12;

function Kanban({ colunas }: { colunas: ColunaMonitor[] }) {
  const mover = useMoverStatus();
  // O cartão em trânsito vive num ref: `dataTransfer` só devolve o conteúdo no
  // drop, e a coluna precisa saber ANTES (no dragover) se aceita ou não.
  const arrastado = useRef<OsNoQuadro | null>(null);
  const [origem, setOrigem] = useState<string | null>(null);
  const [modal, setModal] = useState<{ osNumber: number; statusInicial?: string | undefined } | null>(
    null,
  );
  const pan = usePanHorizontal();
  // Menu do botão direito: a OS clicada e o ponto da tela onde abrir.
  const [menu, setMenu] = useState<{ os: OsNoQuadro; x: number; y: number } | null>(null);

  // O agrupamento é caro com ~600 OS e o monitor refaz a busca a cada 30 s.
  // Sem memo, cada re-render do componente pai reordenaria tudo de novo.
  const { etapas, porStatus } = useMemo(() => {
    const mapa = new Map<string, OsNoQuadro[]>();

    for (const coluna of colunas) {
      for (const os of coluna.ordens) {
        const item: OsNoQuadro = { ...os, tecnicoNome: coluna.tecnicoNome };
        const lista = mapa.get(os.status);
        if (lista) lista.push(item);
        else mapa.set(os.status, [item]);
      }
    }

    // Mais recente primeiro: o topo da coluna é o que acabou de entrar na
    // etapa. Empate de dias cai para o número da OS, que é crescente.
    for (const lista of mapa.values()) {
      lista.sort(
        (a, b) => (a.diasEmAberto ?? 0) - (b.diasEmAberto ?? 0) || b.osNumber - a.osNumber,
      );
    }

    const temIndefinida = (mapa.get(STATUS_INDEFINIDO.codigo)?.length ?? 0) > 0;
    return {
      porStatus: mapa,
      etapas: temIndefinida ? [STATUS_INDEFINIDO, ...ETAPAS] : ETAPAS,
    };
  }, [colunas]);

  function terminarArraste(): void {
    arrastado.current = null;
    setOrigem(null);
  }

  /**
   * Manda a OS para uma etapa — vale para o cartão solto numa coluna e para o
   * menu do botão direito.
   *
   * Etapa que exige pagamento ou data de saída não pode ser gravada só com o
   * gesto: gravar "Pago" sem valor deixaria o caixa mentindo. Nesses casos o
   * modal abre com a etapa já escolhida para o operador completar os campos —
   * as mesmas regras condicionais da tela de status, que vêm do catálogo.
   */
  function moverPara(os: OsNoQuadro, etapa: OsStatus): void {
    if (os.status === etapa.codigo) return;

    if (etapa.exigePagamento || etapa.exigeSaida) {
      setModal({ osNumber: os.osNumber, statusInicial: etapa.codigo });
      return;
    }

    mover.mutate({ osNumber: os.osNumber, status: etapa.codigo, saida: null, pagamento: null });
  }

  /** Soltou o cartão numa coluna. */
  function soltarEm(etapa: OsStatus): void {
    const os = arrastado.current;
    terminarArraste();
    if (os) moverPara(os, etapa);
  }

  return (
    <>
      {mover.isError && (
        <div className="mb-4">
          <ErroNaTela erro={mover.error} />
        </div>
      )}

      {/* Sangra até a borda da tela: o quadro é largo e a margem do `main` só
          encurtaria a área útil de rolagem. */}
      <div
        {...pan.props}
        className={cx(
          '-mx-4 overflow-x-auto px-4 pb-2 scrollbar-creme sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8',
          // No celular a rolagem para em cima de cada etapa, em vez de deixar
          // o operador entre duas colunas. Desligado em `lg`, onde o quadro é
          // arrastado com o mouse (`usePanHorizontal`) e o snap brigaria com o
          // gesto, travando o quadro a cada coluna.
          'snap-x snap-mandatory overscroll-x-contain lg:snap-none',
          pan.arrastando ? 'cursor-grabbing select-none' : 'cursor-grab',
        )}
      >
        <div className="flex min-w-max gap-3 sm:gap-4">
          {etapas.map((etapa) => (
            <ColunaKanban
              key={etapa.codigo}
              etapa={etapa}
              ordens={porStatus.get(etapa.codigo) ?? []}
              origem={origem}
              aoArrastar={(os) => {
                arrastado.current = os;
                setOrigem(os.status);
              }}
              aoLargar={terminarArraste}
              aoSoltar={() => soltarEm(etapa)}
              aoAbrir={(osNumber) => setModal({ osNumber })}
              aoMenu={(os, x, y) => setMenu({ os, x, y })}
            />
          ))}
        </div>
      </div>

      {menu && (
        <MenuCartao
          os={menu.os}
          x={menu.x}
          y={menu.y}
          aoFechar={() => setMenu(null)}
          aoMover={(etapa) => {
            setMenu(null);
            moverPara(menu.os, etapa);
          }}
          aoAbrir={() => {
            setMenu(null);
            setModal({ osNumber: menu.os.osNumber });
          }}
        />
      )}

      {modal && (
        <ModalOs
          osNumber={modal.osNumber}
          statusInicial={modal.statusInicial}
          aoFechar={() => setModal(null)}
        />
      )}
    </>
  );
}

function ColunaKanban({
  etapa,
  ordens,
  origem,
  aoArrastar,
  aoLargar,
  aoSoltar,
  aoAbrir,
  aoMenu,
}: {
  etapa: OsStatus;
  ordens: OsNoQuadro[];
  /** Status de onde o cartão em trânsito saiu; `null` quando ninguém arrasta. */
  origem: string | null;
  aoArrastar: (os: OsNoQuadro) => void;
  aoLargar: () => void;
  aoSoltar: () => void;
  aoAbrir: (osNumber: number) => void;
  aoMenu: (os: OsNoQuadro, x: number, y: number) => void;
}) {
  const tom = tomStatus(etapa.codigo);
  const [visiveis, setVisiveis] = useState(LOTE);
  const [sobre, setSobre] = useState(false);
  const rolagem = useRef<HTMLDivElement>(null);
  const sentinela = useRef<HTMLDivElement>(null);
  // `dragleave` dispara ao passar por CADA filho da coluna. Sem contar as
  // entradas, o destaque piscaria a cada cartão sobrevoado.
  const profundidade = useRef(0);

  const total = ordens.length;
  const temMais = visiveis < total;
  const recebe = origem !== null && origem !== etapa.codigo;

  /**
   * Rolagem infinita: a coluna "Aguardando Orçamento" sozinha tem centenas de
   * OS, e montar todos os cartões de uma vez trava a TV da oficina. Renderiza
   * um lote e observa uma sentinela no fim da lista — quando ela entra na área
   * visível DA COLUNA (`root`), entra o lote seguinte.
   */
  useEffect(() => {
    const alvo = sentinela.current;
    if (!alvo || !temMais) return;

    const observador = new IntersectionObserver(
      (entradas) => {
        if (entradas.some((e) => e.isIntersecting)) {
          setVisiveis((v) => Math.min(v + LOTE, total));
        }
      },
      { root: rolagem.current, rootMargin: '300px' },
    );

    observador.observe(alvo);
    return () => observador.disconnect();
  }, [temMais, total]);

  // A cada 30 s a lista é substituída. Encolhe a janela quando a fila diminui,
  // mas nunca desmonta o que o operador já tinha rolado.
  useEffect(() => {
    setVisiveis((v) => Math.max(LOTE, Math.min(v, total)));
  }, [total]);

  // O arraste pode terminar fora de qualquer coluna (Esc, soltar no vazio):
  // sem isto o destaque ficaria aceso até o próximo gesto.
  useEffect(() => {
    if (origem === null) {
      profundidade.current = 0;
      setSobre(false);
    }
  }, [origem]);

  return (
    <section
      onDragOver={(e) => {
        if (!recebe) return;
        // Sem `preventDefault` no dragover o navegador recusa o drop.
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      }}
      onDragEnter={() => {
        if (!recebe) return;
        profundidade.current += 1;
        setSobre(true);
      }}
      onDragLeave={() => {
        if (!recebe) return;
        profundidade.current = Math.max(0, profundidade.current - 1);
        if (profundidade.current === 0) setSobre(false);
      }}
      onDrop={(e) => {
        if (!recebe) return;
        e.preventDefault();
        profundidade.current = 0;
        setSobre(false);
        aoSoltar();
      }}
      className={cx(
        // No celular a coluna ocupa quase a tela toda, deixando à mostra uma
        // fatia da próxima — é ela que diz que o quadro anda para o lado.
        // A altura desconta menos cabeçalho no celular, onde o topo é enxuto.
        'flex h-[calc(100dvh-13rem)] w-[85vw] max-w-80 shrink-0 snap-start flex-col overflow-hidden rounded-xl',
        'sm:h-[calc(100dvh-15rem)] sm:min-h-96 sm:w-80',
        'ring-1 transition-colors',
        // Alvo de soltar é ação do operador: quem sinaliza é o âmbar da marca,
        // não a cor da etapa — assim o destaque é o mesmo em qualquer coluna.
        sobre ? 'bg-brand-50 ring-brand-400' : 'bg-creme-50 ring-creme-200',
      )}
    >
      {/* Filete da etapa no topo: identifica a coluna antes de ler o título. */}
      <div className="h-0.5 shrink-0" style={{ backgroundColor: tom.forte }} />

      <header className="shrink-0 border-b border-creme-200 px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <span
              aria-hidden="true"
              className="h-1.5 w-1.5 shrink-0 rounded-full"
              style={{ backgroundColor: tom.forte }}
            />
            <h2 className="truncate text-[13px] font-semibold tracking-wide text-stone-800">
              {etapa.rotulo}
            </h2>
          </div>
          <span
            className="shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums"
            style={{ backgroundColor: tom.suave, color: tom.texto }}
          >
            {total}
          </span>
        </div>
        <p className="mt-1 text-[10px] font-medium uppercase tracking-widest text-stone-400">
          {etapa.categoria}
        </p>
      </header>

      {total === 0 ? (
        <p
          className={cx(
            'm-4 rounded-lg border border-dashed px-4 py-8 text-center text-xs transition-colors',
            recebe ? 'border-brand-400 text-brand-700' : 'border-creme-300 text-stone-400',
          )}
        >
          {recebe ? 'Solte aqui para mover' : 'Nenhuma OS nesta etapa.'}
        </p>
      ) : (
        <div ref={rolagem} className="scrollbar-creme flex-1 overflow-y-auto p-3">
          <Cascata className="flex flex-col gap-2" chave={etapa.codigo}>
            {ordens.slice(0, visiveis).map((os) => (
              <CartaoKanban
                key={os.osNumber}
                os={os}
                tom={tom}
                aoArrastar={aoArrastar}
                aoLargar={aoLargar}
                aoAbrir={aoAbrir}
                aoMenu={aoMenu}
              />
            ))}
          </Cascata>

          {temMais && (
            <div ref={sentinela} className="py-4 text-center text-[11px] text-stone-400">
              carregando mais… ({visiveis} de {total})
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function CartaoKanban({
  os,
  tom,
  aoArrastar,
  aoLargar,
  aoAbrir,
  aoMenu,
}: {
  os: OsNoQuadro;
  tom: TomStatus;
  aoArrastar: (os: OsNoQuadro) => void;
  aoLargar: () => void;
  aoAbrir: (osNumber: number) => void;
  aoMenu: (os: OsNoQuadro, x: number, y: number) => void;
}) {
  const parada = (os.diasEmAberto ?? 0) >= PARADA_HA_MUITO;
  const aparelho = [os.aparelho, os.marca, os.modelo].filter(Boolean).join(' · ');
  const cliente = `${os.nome} ${os.sobrenome}`.trim();
  const toque = usePonteiroGrosso();

  return (
    // Invólucro para o cartão e o botão de ações conviverem: botão dentro de
    // botão é HTML inválido, e o navegador desmonta a árvore.
    <div
      className={cx(
        'group relative overflow-hidden rounded-lg bg-white ring-1 transition-colors duration-150',
        parada ? 'ring-brand-200' : 'ring-creme-200 hover:ring-creme-400',
      )}
    >
      {/* Filete lateral no tom da etapa: dá a leitura da coluna mesmo quando o
          cartão é lido isolado, sem repetir o rótulo do status. */}
      <span
        aria-hidden="true"
        className="absolute inset-y-0 left-0 z-10 w-0.5"
        style={{ backgroundColor: tom.forte }}
      />

      {/* Botão, e não link: um `<a>` arrastável leva a URL junto no
          `dataTransfer` e alguns navegadores tratam o gesto como "arrastar
          link". Quem quiser a tela cheia chega nela pelo modal. */}
      <button
        type="button"
        /**
         * Arrastável só com mouse.
         *
         * O toque nunca dispara `dragstart` — mover de etapa no celular é pelo
         * botão de ações ao lado. Deixar `draggable` ligado não habilitava
         * nada e ainda custava caro: o navegador passa a tratar o primeiro
         * toque como possível início de arraste e engole o clique, que é o
         * "preciso tocar duas vezes" do quadro.
         */
        draggable={!toque}
        onDragStart={(e) => {
          // Alguns navegadores só iniciam o arraste se houver dado no evento; o
          // que a coluna consulta de verdade é o ref do quadro.
          e.dataTransfer.setData('text/plain', String(os.osNumber));
          e.dataTransfer.effectAllowed = 'move';
          aoArrastar(os);
        }}
        onDragEnd={aoLargar}
        onClick={() => aoAbrir(os.osNumber)}
        onContextMenu={(e) => {
          // Sem isto o navegador abriria o menu dele por cima do nosso.
          e.preventDefault();
          aoMenu(os, e.clientX, e.clientY);
        }}
        className={cx(
          'block w-full py-2.5 pr-3 pl-3.5 text-left transition-colors duration-150 hover:bg-creme-50',
          // A mãozinha de "pode arrastar" só faz sentido onde dá para arrastar.
          toque ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing',
        )}
        title={`OS ${os.osNumber} — ${os.statusRotulo} — entrada ${formatarDataHora(os.entrada)}`}
      >
        {/* `pr-7` reserva o canto do botão de ações, que flutua por cima. */}
        <div className="flex items-start justify-between gap-2 pr-7">
          <h3 className="min-w-0 flex-1 truncate text-[13px] font-semibold text-stone-800">
            {cliente || 'Cliente sem nome'}
          </h3>
          {/* Só a OS parada ganha marca. Uma etiqueta em todo cartão vira ruído:
              no fim, nenhuma chama atenção. */}
          {parada && <SeloParada />}
        </div>

        <dl className="mt-1.5 space-y-0.5 text-[11px] leading-tight text-stone-500">
          <div className="flex items-center gap-1.5">
            <dt className="sr-only">Aparelho</dt>
            <IconeAparelho />
            <dd className="truncate">{aparelho || 'Aparelho não informado'}</dd>
          </div>
          <div className="flex items-center gap-1.5">
            <dt className="sr-only">Entrada</dt>
            <IconeCalendario />
            <dd className="tabular-nums">{formatarData(os.entrada) || '—'}</dd>
          </div>
        </dl>

        <div className="mt-2.5 flex items-center justify-between gap-2 border-t border-creme-200 pt-2">
          <span className="text-[13px] font-bold tabular-nums text-stone-700">
            <span className="text-[10px] font-medium uppercase tracking-wider text-stone-400">OS </span>
            {os.osNumber}
          </span>

          <div className="flex items-center gap-2">
            <span
              className={cx(
                'text-[11px] tabular-nums',
                parada ? 'font-semibold text-brand-700' : 'text-stone-400',
              )}
            >
              {descreverDias(os.diasEmAberto)}
            </span>
            <span
              title={os.tecnicoNome}
              className="flex h-5 w-5 items-center justify-center rounded-full bg-creme-100 text-[9px] font-bold uppercase text-stone-500 ring-1 ring-creme-300"
            >
              {iniciais(os.tecnicoNome)}
            </span>
          </div>
        </div>
      </button>

      {/**
       * Ações do cartão.
       *
       * Mover de etapa era só arrastar-e-soltar, e `dragstart` não existe no
       * toque: no celular o quadro era somente leitura. Este botão abre o mesmo
       * menu do clique direito, onde a etapa está a um toque — inclusive as que
       * nem têm coluna no quadro.
       *
       * Sempre visível no dedo (não há `hover` para revelá-lo); no mouse
       * aparece ao passar pelo cartão ou ao receber foco pelo teclado.
       */}
      <button
        type="button"
        aria-label={`Ações da OS ${os.osNumber}`}
        onClick={(e) => {
          const caixa = e.currentTarget.getBoundingClientRect();
          // Ancorado na quina de baixo do botão: o menu desce a partir dele,
          // como um menu suspenso, em vez de nascer sob o dedo.
          aoMenu(os, caixa.right, caixa.bottom);
        }}
        className={cx(
          'absolute top-1 right-1 flex h-7 w-7 items-center justify-center rounded-md text-stone-400',
          'transition-colors hover:bg-creme-200 hover:text-stone-700 active:bg-creme-200',
          'lg:opacity-0 lg:group-hover:opacity-100 lg:focus-visible:opacity-100',
        )}
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
          <circle cx="12" cy="5" r="1.6" />
          <circle cx="12" cy="12" r="1.6" />
          <circle cx="12" cy="19" r="1.6" />
        </svg>
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Menu do botão direito                                               */
/* ------------------------------------------------------------------ */

/** Margem mínima entre o menu e a borda da janela, ao virar de lado. */
const FOLGA_MENU = 8; // px

/**
 * Menu de contexto do cartão.
 *
 * O arrastar-e-soltar só alcança as colunas visíveis do quadro; com quinze
 * etapas no catálogo, mandar uma OS para "Aguardando Peça" exigia rolar o
 * quadro inteiro com o cartão preso ao mouse. Pelo menu a etapa está sempre a
 * um clique, inclusive as que nem têm coluna (as que finalizam a OS).
 *
 * As regras continuam as do catálogo: etapa que pede valor ou data de saída
 * abre o modal em vez de gravar direto — o menu só aponta isso ao operador.
 */
function MenuCartao({
  os,
  x,
  y,
  aoFechar,
  aoMover,
  aoAbrir,
}: {
  os: OsNoQuadro;
  x: number;
  y: number;
  aoFechar: () => void;
  aoMover: (etapa: OsStatus) => void;
  aoAbrir: () => void;
}) {
  const painel = useRef<HTMLDivElement>(null);
  const [posicao, setPosicao] = useState({ x, y });

  // Aberto perto da borda direita ou do rodapé, o menu sairia da tela: mede
  // depois de montado (a altura depende da lista de etapas) e vira de lado.
  useLayoutEffect(() => {
    const caixa = painel.current?.getBoundingClientRect();
    if (!caixa) return;

    setPosicao({
      x: Math.max(FOLGA_MENU, Math.min(x, window.innerWidth - caixa.width - FOLGA_MENU)),
      y: Math.max(FOLGA_MENU, Math.min(y, window.innerHeight - caixa.height - FOLGA_MENU)),
    });
  }, [x, y]);

  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') aoFechar();
    };
    document.addEventListener('keydown', aoTeclar);
    // O quadro rola atrás do menu; ancorado em coordenadas de tela, ele
    // descolaria do cartão. Mais honesto fechar.
    window.addEventListener('resize', aoFechar);
    return () => {
      document.removeEventListener('keydown', aoTeclar);
      window.removeEventListener('resize', aoFechar);
    };
  }, [aoFechar]);

  const cliente = `${os.nome} ${os.sobrenome}`.trim() || 'Cliente sem nome';

  return createPortal(
    <div
      className="fixed inset-0 z-50"
      onClick={aoFechar}
      onContextMenu={(e) => {
        e.preventDefault();
        aoFechar();
      }}
      onWheel={aoFechar}
    >
      <div
        ref={painel}
        role="menu"
        aria-label={`Ações da OS ${os.osNumber}`}
        onClick={(e) => e.stopPropagation()}
        style={{ left: posicao.x, top: posicao.y }}
        className="fixed w-64 overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-creme-300"
        // O menu abre por cima do quadro, que rola: sem isto o gesto de rolar a
        // lista de etapas até o fim arrasta o quadro atrás junto.
        onTouchMove={(e) => e.stopPropagation()}
      >
        <header className="border-b border-creme-200 bg-creme-50 px-3 py-2">
          <p className="truncate text-[13px] font-semibold text-stone-800">{cliente}</p>
          <p className="mt-0.5 flex items-center gap-1.5 text-[11px] text-stone-400">
            <span className="font-bold tabular-nums text-stone-600">OS {os.osNumber}</span>
            <span aria-hidden="true">·</span>
            <span className="truncate">{os.statusRotulo}</span>
          </p>
        </header>

        <p className="px-3 pt-2 text-[10px] font-semibold uppercase tracking-widest text-stone-400">
          Mover para etapa
        </p>
        {/* `40dvh` no celular: a lista tem quinze etapas e uma altura fixa de
            16 rem empurraria o rodapé do menu para fora da tela deitada. */}
        <div className="max-h-[40dvh] overflow-y-auto overscroll-contain scrollbar-creme py-1 sm:max-h-64">
          {OS_STATUS.filter((etapa) => etapa.codigo !== os.status).map((etapa) => (
            <button
              key={etapa.codigo}
              type="button"
              role="menuitem"
              onClick={() => aoMover(etapa)}
              className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[13px] text-stone-600 transition-colors hover:bg-creme-100 hover:text-stone-900 active:bg-creme-100 sm:py-1.5"
            >
              <span
                aria-hidden="true"
                className="h-1.5 w-1.5 shrink-0 rounded-full"
                style={{ backgroundColor: tomStatus(etapa.codigo).forte }}
              />
              <span className="flex-1 truncate">{etapa.rotulo}</span>
              {(etapa.exigePagamento || etapa.exigeSaida) && (
                <span
                  title="Pede valor ou data de saída — abre a ficha da OS"
                  className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-stone-400"
                >
                  ficha
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="border-t border-creme-200 py-1">
          <button
            type="button"
            role="menuitem"
            onClick={aoAbrir}
            className="block w-full px-3 py-2.5 text-left text-[13px] text-stone-600 transition-colors hover:bg-creme-100 hover:text-stone-900 active:bg-creme-100 sm:py-1.5"
          >
            Abrir ficha da OS
          </button>
          <Link
            to="/os/$osNumber/orcamento"
            params={{ osNumber: String(os.osNumber) }}
            role="menuitem"
            className="block px-3 py-2.5 text-[13px] text-stone-600 transition-colors hover:bg-creme-100 hover:text-stone-900 active:bg-creme-100 sm:py-1.5"
          >
            Orçamento
          </Link>
          <Link
            to="/os/$osNumber"
            params={{ osNumber: String(os.osNumber) }}
            role="menuitem"
            className="block px-3 py-2.5 text-[13px] text-stone-600 transition-colors hover:bg-creme-100 hover:text-stone-900 active:bg-creme-100 sm:py-1.5"
          >
            Abrir OS completa
          </Link>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** 'Não atribuído' → 'NA'; 'Claudinei' → 'CL'. */
function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';
  if (partes.length === 1) return partes[0]!.slice(0, 2);
  return `${partes[0]![0]!}${partes[1]![0]!}`;
}

/**
 * Marca da OS parada há tempo demais — o único âmbar dentro do cartão, porque
 * é a única informação que pede ação de quem está olhando o quadro.
 */
function SeloParada() {
  return (
    <span
      title={`Parada há ${PARADA_HA_MUITO} dias ou mais`}
      className="flex shrink-0 items-center gap-1 rounded-full bg-brand-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-brand-700 ring-1 ring-brand-200"
    >
      <span aria-hidden="true" className="h-1 w-1 rounded-full bg-brand-600" />
      parada
    </span>
  );
}

function IconeAparelho() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-3 w-3 shrink-0 text-stone-400"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <rect x="4" y="3" width="16" height="14" rx="2" />
      <path d="M8 21h8" strokeLinecap="round" />
    </svg>
  );
}

function IconeCalendario() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-3 w-3 shrink-0 text-stone-400"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" strokeLinecap="round" />
    </svg>
  );
}
